import { useRef } from 'react'
import { Bold, Italic, Link as LinkIcon, List } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { MarkdownContent } from './markdown-content'
import { cn } from '@/lib/utils'

interface MarkdownEditorProps {
  value: string
  onChange: (next: string) => void
  onBlur?: () => void
  placeholder?: string
  rows?: number
  /** Optional raw-character cap (e.g. 500 for profile bios). Renders a counter. */
  maxChars?: number
  /** Render the preview as `prose-xs` for tight surfaces (step description) vs `prose-sm`. */
  compact?: boolean
  className?: string
}

/**
 * Phase 11b: textarea + always-visible live-preview pane below. Wraps {@link MarkdownContent} —
 * same renderer + sanitization as everywhere else, so what the editor previews is exactly what
 * the viewer surface will render.
 */
export const MarkdownEditor = ({
  value,
  onChange,
  onBlur,
  placeholder,
  rows = 3,
  maxChars,
  compact,
  className,
}: MarkdownEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const overCap = maxChars != null && value.length > maxChars

  const wrapSelection = (before: string, after: string = before) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = value.slice(start, end)
    onChange(value.slice(0, start) + before + selected + after + value.slice(end))
    requestAnimationFrame(() => {
      ta.focus()
      ta.setSelectionRange(start + before.length, start + before.length + selected.length)
    })
  }

  const insertLink = () => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = value.slice(start, end) || 'text'
    const inserted = `[${selected}](url)`
    onChange(value.slice(0, start) + inserted + value.slice(end))
    requestAnimationFrame(() => {
      ta.focus()
      const urlStart = start + inserted.length - 4
      ta.setSelectionRange(urlStart, urlStart + 3)
    })
  }

  const prefixLines = (prefix: string) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const block = value.slice(lineStart, end || start)
    const prefixed = block.length === 0 ? prefix : block.replace(/^/gm, prefix)
    onChange(value.slice(0, lineStart) + prefixed + value.slice(end || start))
    requestAnimationFrame(() => {
      ta.focus()
      const cursor = (end || start) + (prefixed.length - block.length)
      ta.setSelectionRange(cursor, cursor)
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.metaKey || e.ctrlKey)) return
    if (e.key === 'b') {
      e.preventDefault()
      wrapSelection('**')
    } else if (e.key === 'i') {
      e.preventDefault()
      wrapSelection('*')
    } else if (e.key === 'k') {
      e.preventDefault()
      insertLink()
    }
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-1 rounded-md border bg-muted/30 px-1 py-1">
        <ToolbarButton onClick={() => wrapSelection('**')} label="Bold (⌘B)">
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => wrapSelection('*')} label="Italic (⌘I)">
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={insertLink} label="Link (⌘K)">
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => prefixLines('- ')} label="List">
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        className={cn('text-sm', compact && 'text-xs')}
      />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <a
          href="https://www.markdownguide.org/cheat-sheet/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          Markdown supported · cheat sheet
        </a>
        {maxChars != null && (
          <span className={cn('tabular-nums', overCap && 'text-destructive font-medium')}>
            {value.length}/{maxChars}
          </span>
        )}
      </div>
      {value.trim().length > 0 && (
        <div className="rounded-md border bg-muted/30 px-3 py-2">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </p>
          <MarkdownContent value={value} className={compact ? 'prose-xs' : undefined} />
        </div>
      )}
    </div>
  )
}

interface ToolbarButtonProps {
  onClick: () => void
  label: string
  children: React.ReactNode
}

const ToolbarButton = ({ onClick, label, children }: ToolbarButtonProps) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    onMouseDown={(e) => e.preventDefault()}
    className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
  >
    {children}
  </button>
)
